import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ClipboardCheck, Thermometer, AlertTriangle, Truck, Wrench, Wheat, ShieldCheck,
  GraduationCap, Building2, ListChecks, Sparkles, FileText, History, Users, Settings, CreditCard, LogOut,
  Menu, X, Crown, CalendarDays, Tag, ClipboardList, BarChart3, Snowflake, Bug,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import AlertsBell from "@/components/AlertsBell";
import { Brand } from "@/components/Brand";
import { ImpersonationBanner, PaymentReminder, DemoBanner } from "@/components/PlatformTools";
import OfflineSync from "@/components/OfflineSync";
import { DemoTour, DemoSignupPrompt, DemoHint } from "@/components/DemoTour";

export const NAV = [
  { group: "Today", items: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/checks", label: "Today's checks", icon: ClipboardCheck },
    { to: "/app/temps", label: "Temperatures", icon: Thermometer },
    { to: "/app/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/app/deliveries", label: "Deliveries", icon: Truck },
    { to: "/app/prep", label: "Prep & labels", icon: Tag, hint: "Try this", tip: "Record a batch and print its use-by and allergen label" },
    { to: "/app/cooling", label: "Cooling & reheating", icon: Snowflake },
  ]},
  { group: "Records", items: [
    { to: "/app/haccp", label: "HACCP plans", icon: ShieldCheck },
    { to: "/app/self-inspections", label: "Self-inspections", icon: ClipboardList },
    { to: "/app/contractors", label: "Pest & maintenance", icon: Bug },
    { to: "/app/allergens", label: "Allergens", icon: Wheat, hint: "New", tip: "Allergen matrix plus a QR menu your customers can scan" },
    { to: "/app/equipment", label: "Equipment", icon: Wrench },
    { to: "/app/training", label: "Training", icon: GraduationCap },
    { to: "/app/suppliers", label: "Suppliers", icon: Building2 },
    { to: "/app/assistant", label: "Ask AI", icon: Sparkles },
  ]},
  { group: "Manage", items: [
    { to: "/app/records", label: "Records & calendar", icon: CalendarDays, perm: "admin", hint: "Try this", tip: "Colour-coded calendar, PDF export and inspector share links" },
    { to: "/app/insights", label: "Insights", icon: BarChart3, perm: "admin" },
    { to: "/app/templates", label: "Checklists setup", icon: ListChecks, perm: "manage" },
    { to: "/app/audit-pack", label: "EHO audit pack", icon: FileText, perm: "audit", hint: "New", tip: "A print-ready pack for your next EHO visit" },
    { to: "/app/audit-log", label: "Audit trail", icon: History, perm: "audit" },
    { to: "/app/team", label: "Team", icon: Users, perm: "admin" },
    { to: "/app/settings", label: "Settings & venues", icon: Settings, perm: "admin" },
    { to: "/app/billing", label: "Billing", icon: CreditCard, perm: "billing" },
    { to: "/app/platform", label: "Platform admin", icon: Crown, platform: true },
  ]},
];

const BOTTOM = [NAV[0].items[1], NAV[0].items[2], NAV[0].items[3], NAV[0].items[0]];

function NavItems({ onNavigate }) {
  const { can, user } = useAuth();
  const inBusiness = !!user.business_id;
  return NAV.map((g) => {
    const items = g.items.filter((i) => (i.platform ? user.platform_role === "admin" && !user.impersonated_by : inBusiness && (!i.perm || can(i.perm))));
    if (!items.length) return null;
    return (
    <div key={g.group} className="mb-6">
      <div className="mb-2 px-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{g.group}</div>
      {items.map((i) => (
        <NavLink key={i.to} to={i.to} end={i.end} onClick={onNavigate}
          data-testid={`nav-${i.to.split("/").pop() || "app"}-link`}
          className={({ isActive }) => cn("ka-press group mb-0.5 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium",
            isActive ? "bg-emerald-500/15 text-emerald-300" : "text-slate-300 hover:bg-white/5 hover:text-white")}>
          <i.icon className="h-4 w-4 shrink-0" />{i.label}
          {i.hint && <DemoHint inline label={i.hint} tip={i.tip} testid={`demo-hint-nav-${i.to.split("/").pop()}`} />}
        </NavLink>
      ))}
    </div>
    );
  });
}

function VenueSelect({ dark }) {
  const { venues, venueId, setVenueId } = useAuth();
  return (
    <select data-testid="venue-select" value={venueId} onChange={(e) => setVenueId(e.target.value)}
      className={cn("w-full rounded-lg border px-3 py-2 text-sm font-medium outline-none",
        dark ? "border-white/10 bg-white/5 text-white" : "border-slate-200 bg-white text-slate-900")}>
      {venues.map((v) => <option key={v.id} value={v.id} className="text-slate-900">{v.name}</option>)}
    </select>
  );
}

function SidebarBody({ onNavigate, showBell }) {
  const { user, business, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 pb-5 pt-6">
        <div className="flex items-center gap-2">
          <Brand dark />
          {showBell && <div className="ml-auto"><AlertsBell dark /></div>}
        </div>
        <div className="mt-5 ka-eyebrow !text-slate-500">{business?.name || "Platform"}</div>
        {business && <div className="mt-2"><VenueSelect dark /></div>}
      </div>
      <nav className="flex-1 overflow-y-auto px-2"><NavItems onNavigate={onNavigate} /></nav>
      <div className="border-t border-white/10 p-4">
        <div className="text-sm font-semibold text-white" data-testid="current-user-name">{user.name}</div>
        <div data-testid="current-user-role" className="font-mono text-[11px] uppercase tracking-wider text-emerald-400">{user.tenant_role || (user.platform_role === "admin" ? "Super-Admin" : "")}</div>
        <button data-testid="logout-button" onClick={async () => { await logout(); nav("/login"); }}
          className="ka-press mt-3 flex items-center gap-2 text-sm text-slate-400 hover:text-white">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );
}

function useIsDesktop() {
  const q = "(min-width: 1024px)";
  const [d, setD] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setD(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return d;
}

export default function AppShell() {
  const [open, setOpen] = useState(false);
  const desktop = useIsDesktop();
  const { business } = useAuth();
  return (
    <div className="min-h-screen bg-[#F6F7F4]">
      <ImpersonationBanner />
      <PaymentReminder />
      <DemoBanner />
      <OfflineSync />
      <DemoTour />
      <DemoSignupPrompt />
      <aside className="no-print fixed inset-y-0 left-0 z-40 hidden w-64 bg-slate-950 lg:block"><SidebarBody showBell={desktop} /></aside>
      <header className="no-print sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200/80 bg-white/85 px-4 py-3 backdrop-blur-md lg:hidden">
        <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} className="ka-press rounded-lg p-2 hover:bg-slate-100"><Menu className="h-5 w-5" /></button>
        <div className="flex items-center"><Brand small /></div>
        <div className="ml-auto flex items-center gap-1">{!desktop && <AlertsBell testid="mobile-alerts-bell-button" />}{business && <div className="w-36"><VenueSelect /></div>}</div>
      </header>
      {open && (
        <div className="no-print fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-slate-950 ka-fade">
            <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="absolute right-3 top-3 p-2 text-slate-400"><X className="h-5 w-5" /></button>
            <SidebarBody onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}
      <main className="print-area px-4 pb-28 pt-6 sm:px-8 lg:ml-64 lg:px-12 lg:pb-12 lg:pt-10">
        <div className="mx-auto max-w-6xl"><Outlet /></div>
      </main>
      {business && <nav className="no-print fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-slate-800 bg-slate-950 pb-[env(safe-area-inset-bottom)] lg:hidden">
        {BOTTOM.map((i) => (
          <NavLink key={i.to} to={i.to} end={i.end} data-testid={`bottom-nav-${i.to.split("/").pop() || "app"}`}
            className={({ isActive }) => cn("ka-press flex flex-col items-center gap-1 py-3 text-[11px] font-medium", isActive ? "text-emerald-400" : "text-slate-400")}>
            <i.icon className="h-5 w-5" />{i.label.replace("Today's checks", "Checks")}
          </NavLink>
        ))}
      </nav>}
    </div>
  );
}
